import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {resolveApiBase} from '../../core/api-base';
import {StepUpContext} from './step-up-context';

export type MfaMethod = 'totp' | 'email';

export interface MfaStateResponse {
  enrolled: boolean;
  /** The FIRST held method (totp before email) or '' — kept for the 47.4 pages that read it. */
  method: MfaMethod | '';
  /** Every held method (Story 47.5) — a user may hold both. */
  methods: MfaMethod[];
  /** The methods THIS application accepts (Story 47.6); absent on older servers = unrestricted. */
  allowed?: MfaMethod[];
  elevated: boolean;
  /**
   * The gate's answer for THIS client (Story 47.6): elevated by a method it
   * accepts, or by a recovery code. Pages resume on this, not on `elevated` —
   * after the prove step the session is elevated by a rejected method.
   */
  satisfied?: boolean;
}

export const ALL_MFA_METHODS: MfaMethod[] = ['totp', 'email'];

/** methods ∩ allowed in the stable order, treating an absent `allowed` as unrestricted. */
export function usableMethods(s: Pick<MfaStateResponse, 'method' | 'methods' | 'allowed'>): MfaMethod[] {
  const held = s.methods?.length ? s.methods : (s.method ? [s.method] : []);
  const allowed = s.allowed?.length ? s.allowed : ALL_MFA_METHODS;
  return ALL_MFA_METHODS.filter(m => held.includes(m) && allowed.includes(m));
}

export interface CodeSentResponse {
  code_sent: boolean;
}

export interface MfaVerifyResponse {
  ok: boolean;
  recovery_codes_left?: number;
}

export interface EnrollStartResponse {
  method: MfaMethod;
  otpauth_url?: string;
  secret?: string;
  /** A `data:image/png;base64,…` URL rendered server-side — never a hosted URL. */
  qr_png?: string;
  code_sent: boolean;
}

export interface EnrollConfirmResponse {
  enabled: boolean;
  method: MfaMethod;
  recovery_codes: string[];
}

export type MfaFactor = {code: string} | {recoveryCode: string};

// MfaService — the end-user step-up API (Story 47.2, identity `internal/mfaflow`).
// Every call is session-authenticated the way /device/* is — by the host-only
// session cookie the browser attaches (Story 53.9; nothing here carries a
// session) — plus client_id so the backend can check the user is bound to the
// client that triggered the step-up.
@Injectable({providedIn: 'root'})
export class MfaService {
  private readonly http = inject(HttpClient);

  state(ctx: StepUpContext): Observable<MfaStateResponse> {
    return this.http.get<MfaStateResponse>(`${resolveApiBase()}/mfa/state`, {
      params: {client_id: ctx.clientId},
    });
  }

  sendCode(ctx: StepUpContext): Observable<CodeSentResponse> {
    return this.post<CodeSentResponse>('/mfa/challenge/send', ctx, {});
  }

  verify(ctx: StepUpContext, factor: MfaFactor): Observable<MfaVerifyResponse> {
    const body: Record<string, string> = 'recoveryCode' in factor
      ? {recovery_code: factor.recoveryCode.trim()}
      : {mfa_code: factor.code.trim()};
    return this.post<MfaVerifyResponse>('/mfa/challenge/verify', ctx, body);
  }

  enrollStart(ctx: StepUpContext, method: MfaMethod): Observable<EnrollStartResponse> {
    return this.post<EnrollStartResponse>('/mfa/enroll/start', ctx, {method});
  }

  enrollResend(ctx: StepUpContext): Observable<CodeSentResponse> {
    return this.post<CodeSentResponse>('/mfa/enroll/resend', ctx, {});
  }

  enrollConfirm(ctx: StepUpContext, code: string): Observable<EnrollConfirmResponse> {
    return this.post<EnrollConfirmResponse>('/mfa/enroll/confirm', ctx, {code: code.trim()});
  }

  private post<T>(path: string, ctx: StepUpContext, body: Record<string, string>): Observable<T> {
    return this.http.post<T>(`${resolveApiBase()}${path}`, {
      client_id: ctx.clientId,
      ...body,
    });
  }
}
